/**
 * #427 W3 — pure formatting/derivation helpers for the Timesheets & Payroll
 * surface. No React, no I/O — unit-testable, and the single home for the
 * money/time math the page renders so the honesty rules (Design Philosophy
 * P5: never lie about data) live in one place.
 *
 * Contract with the server: money is integer CENTS, time is integer MINUTES.
 * A `null` money value means "no rate on file" and must render as an honest
 * blank/flag — NEVER `$0.00`.
 */
import type { OvertimeBasis, TimesheetFlag } from './api.js';

/** CDLE Rule 5.2.3 remedy: a missed paid 10-minute rest = 10/60 of an hour. */
const REST_PERIOD_MINUTES = 10;
const HOUR_MINUTES = 60;

/** `$1,234.56` from integer cents. Callers guard `null` before calling —
 *  this never invents a value for missing data. */
export function formatUsdCents(cents: number): string {
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
}

/** Cents → a plain dollars string for an <input> (e.g. `15.50`). */
export function centsToDollarsInput(cents: number): string {
  return (cents / 100).toFixed(2);
}

/** Parse a dollars string to positive integer cents. Returns null when the
 *  value is empty, non-numeric, or ≤ 0 (the server rejects a $0/negative
 *  rate — we mirror that so the form never submits an invalid rate). */
export function dollarsInputToCents(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const dollars = Number(trimmed);
  if (!Number.isFinite(dollars) || dollars <= 0) return null;
  return Math.round(dollars * 100);
}

/** Minutes → decimal hours string, e.g. 450 → `7.50`. */
export function minutesToHours(minutes: number): string {
  return (minutes / 60).toFixed(2);
}

/**
 * The wages a set of flagged-but-unattested rest periods puts AT RISK — the
 * maximum owed if every one were attested missed. Mirrors the server's
 * `missedRestWageCents` (count × rate × 10/60, rounded). Returns null when
 * there is no rate on file, so the caller says so honestly instead of
 * printing `$0.00` (P5).
 */
export function atRiskCents(
  flaggedCount: number,
  hourlyRateCents: number | null,
): number | null {
  if (hourlyRateCents === null || hourlyRateCents <= 0) return null;
  if (flaggedCount <= 0) return 0;
  return Math.round(
    flaggedCount * (hourlyRateCents * (REST_PERIOD_MINUTES / HOUR_MINUTES)),
  );
}

/** Human label for the winning OT trigger — never the raw enum. */
export function otBasisLabel(basis: OvertimeBasis | null): string {
  switch (basis) {
    case 'weekly':
      return 'Weekly · over 40h';
    case 'daily':
      return 'Daily · over 12h';
    case 'consecutive':
      return 'Consecutive · over 12h';
    default:
      return '—';
  }
}

/** Human label for a row flag — never the raw enum (P: internal identifiers
 *  are never shown raw). */
export function flagLabel(flag: TimesheetFlag): string {
  switch (flag) {
    case 'open_entry':
      return 'Open punch';
    case 'missing_meal_gt5h':
      return 'No meal break on a 5h+ shift';
    case 'no_rate_set':
      return 'No pay rate set';
    default:
      return flag;
  }
}
